/**
 * React Router configuration
 */

import { lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import AllFootprintsPage from '@/pages/AllFootprintsPage';
import { PUBLIC_DOMAIN_ORDER, isAIOEnabled, defaultsToAIO } from '@/config/domainContent';
import type { DomainType } from '@/types';

const DomainPage = lazy(() => import('@/pages/DomainPage'));
const DomainHomePage = lazy(() => import('@/pages/DomainHomePage'));
const GenevaHomePage = lazy(() => import('@/pages/GenevaHomePage'));
const UnAiHomePage = lazy(() => import('@/pages/UnAiHomePage'));
const UnHomePage = lazy(() => import('@/pages/UnHomePage'));
const DiplomaticHomePage = lazy(() => import('@/pages/DiplomaticHomePage'));
const MediaDigitalHomePage = lazy(() => import('@/pages/MediaDigitalHomePage'));
const TechHomePage = lazy(() => import('@/pages/TechHomePage'));
const ListOfTopicsPage = lazy(() => import('@/pages/ListOfTopicsPage'));
const ActorAnalysisPage = lazy(() => import('@/pages/ActorAnalysisPage'));
const ActorRankingPage = lazy(() => import('@/pages/ActorRankingPage'));
const AIOPage = lazy(() => import('@/pages/AIOPage'));

const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminSuggestedTopics = lazy(() => import('@/pages/admin/AdminSuggestedTopics'));
const AdminCategories = lazy(() => import('@/pages/admin/AdminCategories'));
const AdminTerms = lazy(() => import('@/pages/admin/AdminTerms'));
const AdminSerpApi = lazy(() => import('@/pages/admin/AdminSerpApi'));
const AdminDatabase = lazy(() => import('@/pages/admin/AdminDatabase'));
const AdminUpdates = lazy(() => import('@/pages/admin/AdminUpdates'));
const AdminStatus = lazy(() => import('@/pages/admin/AdminStatus'));
const AdminCalculations = lazy(() => import('@/pages/admin/AdminCalculations.tsx'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminProfile = lazy(() => import('@/pages/admin/AdminProfile'));

const domainRoutes = PUBLIC_DOMAIN_ORDER.flatMap((domain: DomainType) => [
  {
    path: `/${domain}-home`,
    element:
      domain === 'geneva' ? (
        <GenevaHomePage />
      ) : domain === 'un-ai' ? (
        <UnAiHomePage />
      ) : domain === 'un' ? (
        <UnHomePage />
      ) : domain === 'diplomatic' ? (
        <DiplomaticHomePage />
      ) : domain === 'mediadigital' ? (
        <MediaDigitalHomePage />
      ) : domain === 'tech' ? (
        <TechHomePage />
      ) : (
        <DomainHomePage domain={domain} />
      ),
  },
  {
    path: `/${domain}-term-search`,
    element: <DomainPage domain={domain} />,
  },
  ...(isAIOEnabled(domain) || defaultsToAIO(domain)
    ? [
        {
          path: `/${domain}-ai-overview`,
          element: <AIOPage domain={domain} />,
        },
      ]
    : []),
  {
    path: `/${domain}-positon-graph`,
    element: <ActorRankingPage domain={domain} />,
  },
  {
    path: `/${domain}-compare`,
    element: <ActorAnalysisPage domain={domain} />,
  },
  {
    path: `/${domain}-topics`,
    element: <ListOfTopicsPage domain={domain} />,
  },
]);

// Create router matching original Dash app URL patterns
const router = createBrowserRouter([
  {
    path: '/',
    element: <AllFootprintsPage />,
  },
  // Admin panel (hidden route)
  {
    path: '/sys-ctrl-panel',
    children: [
      { index: true, element: <AdminLogin /> },
      {
        element: <AdminLayout />,
        children: [
          { path: 'dashboard', element: <AdminDashboard /> },
          { path: 'suggestions', element: <AdminSuggestedTopics /> },
          { path: 'categories', element: <AdminCategories /> },
          { path: 'terms', element: <AdminTerms /> },
          { path: 'users', element: <AdminUsers /> },
          { path: 'serpapi', element: <AdminSerpApi /> },
          { path: 'updates', element: <AdminUpdates /> },
          { path: 'database', element: <AdminDatabase /> },
          { path: 'status', element: <AdminStatus /> },
          { path: 'calculations', element: <AdminCalculations /> },
          { path: 'profile', element: <AdminProfile /> },
        ],
      },
    ],
  },
  ...domainRoutes,
]);

export function AppRoutes() {
  return <RouterProvider router={router} />;
}
